import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import {
  getPickerKpis, getMovementReport, getAdjustments,
  createAdjustment, approveAdjustment, excelExportUrl,
} from '../lib/api';
import { Download, Plus, Check, FileXls, Warning } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';

export default function Reports() {
  const { isManager } = useAuth();
  const [pickerKpis, setPickerKpis] = useState([]);
  const [movements, setMovements] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdjustment, setShowAdjustment] = useState(false);
  const [newAdjustment, setNewAdjustment] = useState({ variant_id: '', location_id: '', quantity_delta: 0, reason: '' });

  const fetchData = async () => {
    try {
      const [kpisRes, movementsRes, adjRes] = await Promise.all([
        getPickerKpis(),
        getMovementReport({}),
        getAdjustments(),
      ]);
      setPickerKpis(kpisRes.data);
      setMovements(movementsRes.data);
      setAdjustments(adjRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreateAdjustment = async () => {
    try {
      await createAdjustment(newAdjustment);
      setShowAdjustment(false);
      setNewAdjustment({ variant_id: '', location_id: '', quantity_delta: 0, reason: '' });
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleApproveAdjustment = async (id) => {
    try {
      await approveAdjustment(id);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  const exportToCSV = (data, filename) => {
    const headers = Object.keys(data[0] || {}).join(',');
    const rows = data.map(row => Object.values(row).join(','));
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
  };

  const downloadExcel = (type) => {
    const link = document.createElement('a');
    link.href = excelExportUrl(type);
    link.download = `${type}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Layout>
      <div data-testid="reports-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Reports & Analytics</h1>
            <p className="text-muted-foreground">View KPIs, movements, and adjustments.</p>
          </div>
          {isManager && (
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => downloadExcel('inventory')} data-testid="export-inventory-xlsx">
                <FileXls size={18} className="mr-2" /> Inventory XLSX
              </Button>
              <Button variant="outline" onClick={() => downloadExcel('movements')} data-testid="export-movements-xlsx">
                <FileXls size={18} className="mr-2" /> Movements XLSX
              </Button>
              <Button variant="outline" onClick={() => downloadExcel('orders')} data-testid="export-orders-xlsx">
                <FileXls size={18} className="mr-2" /> Orders XLSX
              </Button>
              <Link to="/reports/supplier-shortages">
                <Button variant="outline" data-testid="reports-supplier-shortages-link">
                  <Warning size={18} className="mr-2 text-amber-600" weight="fill" /> Supplier shortages
                </Button>
              </Link>
              <Dialog open={showAdjustment} onOpenChange={setShowAdjustment}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Plus size={18} className="mr-2" /> Adjustment
                  </Button>
                </DialogTrigger>
                <DialogContent className="border border-border rounded-none">
                  <DialogHeader>
                    <DialogTitle>Create Adjustment</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="overline">Variant ID</Label>
                      <Input value={newAdjustment.variant_id} onChange={(e) => setNewAdjustment({ ...newAdjustment, variant_id: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label className="overline">Location ID</Label>
                      <Input value={newAdjustment.location_id} onChange={(e) => setNewAdjustment({ ...newAdjustment, location_id: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label className="overline">Quantity (+/-)</Label>
                      <Input type="number" value={newAdjustment.quantity_delta} onChange={(e) => setNewAdjustment({ ...newAdjustment, quantity_delta: parseInt(e.target.value) })} className="mt-1" />
                    </div>
                    <div>
                      <Label className="overline">Reason</Label>
                      <Input value={newAdjustment.reason} onChange={(e) => setNewAdjustment({ ...newAdjustment, reason: e.target.value })} className="mt-1" />
                    </div>
                    <Button onClick={handleCreateAdjustment} className="w-full bg-brand hover:bg-brand/90">Create</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        <Tabs defaultValue="kpis" className="bg-card border border-border rounded-lg">
          <TabsList className="w-full border-b border-border p-0 h-auto bg-transparent rounded-none flex-wrap">
            <TabsTrigger value="kpis" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">Picker KPIs</TabsTrigger>
            <TabsTrigger value="movements" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">Movements</TabsTrigger>
            <TabsTrigger value="adjustments" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">Adjustments</TabsTrigger>
          </TabsList>

          <TabsContent value="kpis" className="p-0 m-0">
            <div className="p-4 border-b border-border flex justify-end">
              <Button variant="outline" size="sm" onClick={() => exportToCSV(pickerKpis, 'picker-kpis')} disabled={pickerKpis.length === 0}>
                <Download size={16} className="mr-2" /> Export CSV
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full table-dense">
                <thead>
                  <tr>
                    <th>Picker</th>
                    <th>Total Picks</th>
                    <th>Completed Lists</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={3} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
                  ) : pickerKpis.length === 0 ? (
                    <tr><td colSpan={3} className="text-center py-8 text-muted-foreground">No picker data</td></tr>
                  ) : pickerKpis.map(kpi => (
                    <tr key={kpi.picker_id}>
                      <td className="font-medium">{kpi.picker_name}</td>
                      <td className="font-mono">{kpi.total_picks}</td>
                      <td className="font-mono">{kpi.completed_lists}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="movements" className="p-0 m-0">
            <div className="p-4 border-b border-border flex justify-end">
              <Button variant="outline" size="sm" onClick={() => exportToCSV(movements, 'movements')} disabled={movements.length === 0}>
                <Download size={16} className="mr-2" /> Export CSV
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full table-dense">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Quantity</th>
                    <th>User</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.slice(0, 50).map((m, i) => (
                    <tr key={i}>
                      <td><span className="uppercase text-xs font-bold text-muted-foreground">{m.movement_type}</span></td>
                      <td className="font-mono">{m.quantity}</td>
                      <td className="font-mono text-xs">{m.user_id?.slice(-8)}</td>
                      <td className="text-sm text-muted-foreground">{new Date(m.timestamp).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="adjustments" className="p-0 m-0">
            <div className="overflow-x-auto">
              <table className="w-full table-dense">
                <thead>
                  <tr>
                    <th>Adjustment #</th>
                    <th>Qty Change</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {adjustments.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No adjustments</td></tr>
                  ) : adjustments.map(adj => (
                    <tr key={adj.id}>
                      <td className="font-mono font-semibold">{adj.adjustment_number}</td>
                      <td className={`font-mono font-bold ${adj.quantity_delta > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {adj.quantity_delta > 0 ? '+' : ''}{adj.quantity_delta}
                      </td>
                      <td>{adj.reason}</td>
                      <td>
                        <span className={`px-2 py-1 text-xs font-bold uppercase ${
                          adj.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {adj.status}
                        </span>
                      </td>
                      <td>
                        {adj.status === 'pending' && isManager && (
                          <Button size="sm" onClick={() => handleApproveAdjustment(adj.id)} className="bg-green-600 hover:bg-green-700">
                            <Check size={14} className="mr-1" /> Approve
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
